// src/helpers/fetchPublishedArcTemplate.ts

import { ApiClient } from '@center-technologies/shared-api-clients';
import { ArcTemplateRow } from 'types/schema';
import type { UnifiedFilters } from 'types/filters';
import type { ArcTemplateDefinition } from 'types/arc';

/**
 * No published arc template matches the lookup: schema-service answered 404 or an empty list.
 * A 404 from `/arc-templates` also covers a template the caller may not view (the route collapses
 * denied and absent), so "not found" is the answer for THIS caller, not proof the row is gone.
 */
export class ArcTemplateNotFoundError extends Error {
  readonly systemName: string;
  readonly version: number | undefined;

  constructor(systemName: string, version?: number) {
    super(
      version !== undefined
        ? `No published arc template found for systemName: ${systemName}, version: ${version}`
        : `No published arc template found for systemName: ${systemName}`
    );
    this.name = 'ArcTemplateNotFoundError';
    this.systemName = systemName;
    this.version = version;
  }
}

/**
 * The lookup failed for a reason other than "not found": a network error, a 5xx, or any other
 * status schema-service answered. Says nothing about whether the template exists.
 */
export class ArcTemplateFetchError extends Error {
  readonly systemName: string;
  /** The HTTP status schema-service answered, or `undefined` when no response arrived. */
  readonly status: number | undefined;

  constructor(systemName: string, status: number | undefined, message: string) {
    super(message);
    this.name = 'ArcTemplateFetchError';
    this.systemName = systemName;
    this.status = status;
  }
}

/**
 * Fetches a published arc template from the schema service — the newest published version,
 * or an exact pinned version when `version` is supplied.
 *
 * @param apiClient      – an initialized ApiClient pointed at your schema endpoint
 * @param systemName     – the unique system name identifier for the arc template
 * @param organizationId – the owning org. REQUIRED for service-to-service callers looking up an
 *   org-owned template: `/arc-templates` derives org scope from caller identity, and for a service
 *   call that means the `organizationId` query param. Omit it and the route falls through to its
 *   identity-scoped branch, where a service principal holds no SpiceDB `view` on an org-owned
 *   template — so the lookup 404s while system-owned templates (which bypass SpiceDB) still
 *   resolve. A user-token caller should omit it; the route uses the token's org.
 * @param version        – pin to this exact published version instead of the newest
 *   (specs/arc-model-v2.md Phase 3, `Schema.defaultArcs[].versionPin`). The version is
 *   validated as published at the point it is authored (`update-schema`), so a pin reaching
 *   this call is expected to still resolve — `isPublished=true` stays in effect either way.
 * @returns a promise resolving to the resolved ArcTemplateRow
 * @throws ArcTemplateNotFoundError when schema-service answers 404 or no published row matches
 * @throws ArcTemplateFetchError when the request fails for any other reason (its `status` is the
 *   HTTP status, `undefined` for a network failure) — never to be read as "not found"
 */
export async function fetchPublishedArcTemplate(
  apiClient: ApiClient,
  systemName: string,
  organizationId?: string,
  version?: number
): Promise<ArcTemplateRow> {
  if (!systemName?.trim()) {
    throw new Error('fetchPublishedArcTemplate: systemName is required');
  }

  // Build query string for published templates
  const qs =
    `?systemName=${encodeURIComponent(systemName)}&isPublished=true` +
    (organizationId ? `&organizationId=${encodeURIComponent(organizationId)}` : '') +
    (version !== undefined ? `&version=${encodeURIComponent(version)}` : '');

  return getNewestArcTemplate(apiClient, `/arc-templates${qs}`, systemName, version);
}

/**
 * Fetches the published arc template an EXISTING arc was deployed from — by its globally unique
 * system name and the arc's pinned version — from schema-service's S2S-only
 * `GET /arc-templates/for-arc` (D144 (a)). Template visibility follows the arc gate: the caller
 * must be a service client and must already have authorized the arc, so the lookup carries no
 * org scope and a cross-tenant (subscribed) template resolves like any other.
 *
 * Unlike {@link fetchPublishedArcTemplate}, `ArcTemplateNotFoundError` here is definitive: nothing
 * the route answers depends on who asked. Only the route's own 404 (`error: arc_template_not_found`)
 * counts; a 404 from anything in front of it (a gateway, a route missing behind a proxy) is an
 * `ArcTemplateFetchError`, so a misrouted request is never remembered as "the arc has no template".
 *
 * @param apiClient  – a SERVICE-token ApiClient pointed at the schema service
 * @param systemName – the arc's `template_system_name`
 * @param version    – the arc's `template_version`; omitted resolves the newest published version
 * @throws ArcTemplateNotFoundError when the route answers that no published row has that name (and version)
 * @throws ArcTemplateFetchError for any other failure (`status` is `undefined` for a network failure)
 */
export async function fetchArcTemplateForArc(
  apiClient: ApiClient,
  systemName: string,
  version?: number
): Promise<ArcTemplateRow> {
  if (!systemName?.trim()) {
    throw new Error('fetchArcTemplateForArc: systemName is required');
  }
  const qs =
    `?systemName=${encodeURIComponent(systemName)}` +
    (version !== undefined ? `&version=${encodeURIComponent(version)}` : '');
  return getNewestArcTemplate(
    apiClient,
    `/arc-templates/for-arc${qs}`,
    systemName,
    version,
    (err) => err?.response?.status === 404 && err?.response?.data?.error === ARC_TEMPLATE_NOT_FOUND
  );
}

/** The `error` of `GET /arc-templates/for-arc`'s 404 body. */
const ARC_TEMPLATE_NOT_FOUND = 'arc_template_not_found';

/** GETs an arc-template list and answers its newest row, classifying every failure. */
async function getNewestArcTemplate(
  apiClient: ApiClient,
  url: string,
  systemName: string,
  version: number | undefined,
  isNotFound: (err: any) => boolean = (err) => err?.response?.status === 404
): Promise<ArcTemplateRow> {
  let templates: ArcTemplateRow[] | undefined;
  try {
    templates = await apiClient.get<ArcTemplateRow[]>(url);
  } catch (err: any) {
    // Never rethrow the Axios error itself: it carries the request, socket and agent, hundreds of
    // lines per call when a caller logs it at scheduler scale. The status is what callers need.
    const status: number | undefined = err?.response?.status;
    if (isNotFound(err)) {
      throw new ArcTemplateNotFoundError(systemName, version);
    }
    throw new ArcTemplateFetchError(
      systemName,
      status,
      `Arc template lookup failed for systemName: ${systemName} (status=${status ?? 'none'}): ${err?.message ?? 'unknown error'}`
    );
  }

  if (!templates?.length) {
    throw new ArcTemplateNotFoundError(systemName, version);
  }

  // Return the highest‐version template (a pinned `version` query already narrows to one row).
  templates.sort((a, b) => b.version - a.version);
  return templates[0];
}

/** Parameters for creating a new arc template. */
export interface CreateArcTemplatePayload {
  name: string;
  systemName: string;
  definition: ArcTemplateDefinition;
  applicability?: UnifiedFilters | Record<string, unknown>;
  isActive?: boolean;
}

/**
 * Creates a new arc template — always as an unpublished draft. The canvas
 * editor's only creation entry point is "New arc", which opens straight into
 * the draft state; publishing is a separate, explicit action from the editor.
 *
 * @param apiClient – an initialized ApiClient pointed at the schema service
 * @param body      – the template fields, created in the caller's active organization
 */
export async function createArcTemplate(
  apiClient: ApiClient,
  body: CreateArcTemplatePayload
): Promise<{ arcTemplate: ArcTemplateRow }> {
  return apiClient.post<{ arcTemplate: ArcTemplateRow }>('/arc-templates/', {
    ...body,
    isPublished: false,
  });
}

/** Parameters for updating an arc template version. */
export interface UpdateArcTemplatePayload {
  applicability?: UnifiedFilters | Record<string, unknown>;
  definition?: ArcTemplateDefinition;
  name?: string;
  isPublished?: boolean;
}

/**
 * Updates a draft arc template version — gated at plain `edit` permission on
 * the server (no elevated permission required for applicability-only edits).
 *
 * @param apiClient  – an initialized ApiClient pointed at the schema service
 * @param systemName – the arc template's system name
 * @param version    – the version number to update
 * @param payload    – fields to update (applicability, definition, name, isPublished)
 */
export async function updateArcTemplate(
  apiClient: ApiClient,
  systemName: string,
  version: number,
  payload: UpdateArcTemplatePayload
): Promise<{ arcTemplate: ArcTemplateRow }> {
  return apiClient.put<{ arcTemplate: ArcTemplateRow }>(
    `/arc-templates/${encodeURIComponent(systemName)}/${version}`,
    payload
  );
}
